import { useEffect, useState } from "react";
import { apiRequest, setCsrfToken } from "./api/client";
import { BuilderPreviewFrame, ProjectPreview, WeddingBuilder } from "./builder/WeddingBuilder";
import { Dashboard } from "./platform/Dashboard";
import { LoginPage } from "./platform/LoginPage";
import { PublicInvitation } from "./public/PublicInvitation";
import { blankWeddingData } from "./data/blank-wedding-data";
import { referenceWeddingData } from "./data/reference-wedding-data";
import { InvitationRenderer } from "./template/renderer/InvitationRenderer";

export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  platformRole: "owner" | "admin" | "none";
}

interface SessionResponse {
  ok: true;
  user: SessionUser | null;
  csrfToken: string | null;
}

function ProtectedRoute({ children }: { children: (user: SessionUser) => React.ReactNode }) {
  const [state, setState] = useState<{ loading: boolean; user: SessionUser | null }>({ loading: true, user: null });
  useEffect(() => {
    let active = true;
    apiRequest<SessionResponse>("/api/auth/session")
      .then((response) => {
        setCsrfToken(response.csrfToken);
        if (active) setState({ loading: false, user: response.user });
      })
      .catch(() => active && setState({ loading: false, user: null }));
    return () => { active = false; };
  }, []);
  if (state.loading) return <div className="platform-loading"><span className="brand-mark">IS</span><p>Loading your workspace…</p></div>;
  if (!state.user) {
    const destination = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, "", `/login?next=${encodeURIComponent(destination)}`);
    return <LoginPage />;
  }
  return children(state.user);
}

function ProjectAdminRoute({ user, slug }: { user: SessionUser; slug: string }) {
  const [projectId, setProjectId] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    apiRequest<{ ok: true; project: { id: string } }>(`/api/project-admin/${encodeURIComponent(slug)}`)
      .then((response) => { if (active) setProjectId(response.project.id); })
      .catch((caught) => { if (active) setError(caught instanceof Error ? caught.message : "Unable to open project admin."); });
    return () => { active = false; };
  }, [slug]);
  if (error) return <main className="platform-loading"><p className="alert error">{error}</p><a href="/dashboard">Return to dashboard</a></main>;
  if (!projectId) return <div className="platform-loading"><span className="brand-mark">IS</span><p>Opening project admin…</p></div>;
  return <WeddingBuilder user={user} projectId={projectId} initialPanel="overview" />;
}

export function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/reference-preview") return <InvitationRenderer data={referenceWeddingData} mode="reference-preview" />;
  if (path === "/blank-preview") return <InvitationRenderer data={blankWeddingData} mode="builder" />;
  if (path === "/login") return <LoginPage />;
  if (path === "/dashboard" || path === "/admin" || path === "/") {
    return <ProtectedRoute>{(user) => <Dashboard user={user} />}</ProtectedRoute>;
  }
  const projectAdminMatch = /^\/([a-z0-9]+(?:-[a-z0-9]+)*)\/admin$/.exec(path);
  if (projectAdminMatch) return <ProtectedRoute>{(user) => <ProjectAdminRoute user={user} slug={projectAdminMatch[1]} />}</ProtectedRoute>;
  const builderMatch = /^\/builder\/([0-9a-f-]{36})$/i.exec(path);
  if (builderMatch) return <ProtectedRoute>{(user) => <WeddingBuilder user={user} projectId={builderMatch[1]} />}</ProtectedRoute>;
  const previewMatch = /^\/preview\/([0-9a-f-]{36})$/i.exec(path);
  if (previewMatch) return <ProtectedRoute>{() => <ProjectPreview projectId={previewMatch[1]} />}</ProtectedRoute>;
  const builderPreviewMatch = /^\/builder-preview\/([0-9a-f-]{36})$/i.exec(path);
  if (builderPreviewMatch) return <ProtectedRoute>{() => <BuilderPreviewFrame projectId={builderPreviewMatch[1]} />}</ProtectedRoute>;
  const slug = path.split("/").filter(Boolean)[0] || "";
  return <PublicInvitation slug={slug} />;
}
